import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import api from '../../utils/api';
import { 
  Star, 
  AlertCircle, 
  Calendar, 
  User, 
  FileText, 
  TrendingUp, 
  RefreshCw,
  Clock,
  CheckCircle2,
  XCircle,
  Filter,
  Search,
  ChevronRight,
  Building,
  Users,
  Award,
  Zap
} from 'lucide-react';
import moment from 'moment';

const Spotlight = () => {
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [stats, setStats] = useState({
    total: 0,
    urgent: 0,
    high: 0,
    ongoing: 0,
    overdue: 0
  });

  useEffect(() => {
    fetchHighProfileCases();
  }, []);

  // Refresh when window gains focus (user returns to tab)
  useEffect(() => {
    const handleFocus = () => {
      fetchHighProfileCases();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const fetchHighProfileCases = async (showRefreshing = false) => {
    try {
      if (showRefreshing) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const response = await api.get('/cases/high-profile');
      const highProfileCases = response.data.data || [];
      setCases(highProfileCases);
      
      // Calculate stats
      const overdueCount = highProfileCases.filter(c => {
        if (!c.dueDate) return false;
        return moment(c.dueDate).isBefore(moment(), 'day');
      }).length;

      setStats({
        total: highProfileCases.length,
        urgent: highProfileCases.filter(c => c.priority === 'Urgent').length,
        high: highProfileCases.filter(c => c.priority === 'High').length,
        ongoing: highProfileCases.filter(c => c.status === 'Ongoing' || c.status === 'Initial Review').length,
        overdue: overdueCount
      });
    } catch (error) {
      console.error('Error fetching high-profile cases:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchHighProfileCases(true);
  };

  const getPriorityColor = (priority) => {
    const colors = {
      Urgent: 'bg-red-500 text-white border-red-600',
      High: 'bg-orange-500 text-white border-orange-600',
      Medium: 'bg-yellow-500 text-white border-yellow-600',
      Low: 'bg-green-500 text-white border-green-600',
    };
    return colors[priority] || 'bg-gray-500 text-white border-gray-600';
  };

  const getStatusColor = (status) => {
    const colors = {
      'Open': 'bg-green-100 text-green-800 border-green-300',
      'Initial Review': 'bg-blue-100 text-blue-800 border-blue-300',
      'Ongoing': 'bg-purple-100 text-purple-800 border-purple-300',
      'Closed – No Case': 'bg-gray-100 text-gray-800 border-gray-300',
      'Closed – Case Created': 'bg-green-100 text-green-800 border-green-300',
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getPriorityDot = (priority) => {
    const colors = {
      Urgent: 'bg-red-500',
      High: 'bg-orange-500',
      Medium: 'bg-yellow-500',
      Low: 'bg-green-500',
    };
    return colors[priority] || 'bg-gray-500';
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return moment(dueDate).isBefore(moment(), 'day');
  };

  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    const days = moment(dueDate).diff(moment(), 'days');
    if (days < 0) return `Overdue by ${Math.abs(days)} day${Math.abs(days) !== 1 ? 's' : ''}`;
    if (days === 0) return 'Due today';
    if (days === 1) return 'Due tomorrow';
    return `Due in ${days} days`;
  };

  // Filter cases
  const filteredCases = cases.filter(caseItem => {
    const matchesSearch = !searchQuery || 
      caseItem.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      caseItem.caseNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      caseItem.description?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesPriority = filterPriority === 'all' || caseItem.priority === filterPriority;
    const matchesStatus = filterStatus === 'all' || caseItem.status === filterStatus;
    
    return matchesSearch && matchesPriority && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading high-profile cases...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-yellow-50 via-yellow-50 to-orange-50 rounded-xl shadow-sm border border-yellow-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="bg-yellow-500 rounded-xl p-3 shadow-lg">
              <Star className="w-8 h-8 text-white fill-current" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-2">
                <span>Spotlight</span>
                <span className="text-yellow-600">High-Profile Cases</span>
              </h1>
              <p className="text-gray-600 mt-1">Critical cases requiring immediate attention and priority handling</p>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm disabled:opacity-50"
            title="Refresh list"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* Statistics Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-yellow-400 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-yellow-100 rounded-lg p-3">
              <Star className="w-6 h-6 text-yellow-600 fill-current" />
            </div>
            <span className="text-2xl font-bold text-gray-900">{stats.total}</span>
          </div>
          <p className="text-sm font-medium text-gray-600">Total High-Profile</p>
          <p className="text-xs text-gray-500 mt-1">All spotlight cases</p>
        </div>

        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-400 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-red-100 rounded-lg p-3">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <span className="text-2xl font-bold text-red-600">{stats.urgent}</span>
          </div>
          <p className="text-sm font-medium text-gray-600">Urgent Priority</p>
          <p className="text-xs text-gray-500 mt-1">Requires immediate action</p>
        </div>

        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-orange-400 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-orange-100 rounded-lg p-3">
              <TrendingUp className="w-6 h-6 text-orange-600" />
            </div>
            <span className="text-2xl font-bold text-orange-600">{stats.high}</span>
          </div>
          <p className="text-sm font-medium text-gray-600">High Priority</p>
          <p className="text-xs text-gray-500 mt-1">Needs attention soon</p>
        </div>

        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-400 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-purple-100 rounded-lg p-3">
              <Clock className="w-6 h-6 text-purple-600" />
            </div>
            <span className="text-2xl font-bold text-purple-600">{stats.ongoing}</span>
          </div>
          <p className="text-sm font-medium text-gray-600">Active Cases</p>
          <p className="text-xs text-gray-500 mt-1">In progress</p>
        </div>

        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-400 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-red-100 rounded-lg p-3">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
            <span className="text-2xl font-bold text-red-600">{stats.overdue}</span>
          </div>
          <p className="text-sm font-medium text-gray-600">Overdue</p>
          <p className="text-xs text-gray-500 mt-1">Past due date</p>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search cases by title, number, or description..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Priorities</option>
              <option value="Urgent">Urgent</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Statuses</option>
              <option value="Open">Open</option>
              <option value="Initial Review">Initial Review</option>
              <option value="Ongoing">Ongoing</option>
              <option value="Closed – No Case">Closed – No Case</option>
              <option value="Closed – Case Created">Closed – Case Created</option>
            </select>
            {(searchQuery || filterPriority !== 'all' || filterStatus !== 'all') && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setFilterPriority('all');
                  setFilterStatus('all');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
              >
                Clear
              </button>
            )}
          </div>
        </div>
        {filteredCases.length !== cases.length && (
          <p className="text-sm text-gray-600 mt-3">
            Showing {filteredCases.length} of {cases.length} cases
          </p>
        )}
      </div>

      {/* High-Profile Cases Grid */}
      {filteredCases.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-12 text-center">
          <div className="max-w-md mx-auto">
            <div className="bg-gray-100 rounded-full p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center">
              <Star className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {cases.length === 0 ? 'No High-Profile Cases' : 'No Cases Match Your Filters'}
            </h3>
            <p className="text-gray-500 mb-6">
              {cases.length === 0 
                ? 'There are currently no cases marked as high-profile. High-profile cases are typically urgent or critical cases that require special attention.'
                : 'Try adjusting your search or filter criteria to find cases.'}
            </p>
            {(user?.role === 'Administrator' || user?.role === 'Supervisor') && cases.length === 0 && (
              <Link
                to="/cases"
                className="inline-flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md"
              >
                <FileText className="w-4 h-4" />
                <span>Go to Cases</span>
              </Link>
            )}
            {cases.length > 0 && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setFilterPriority('all');
                  setFilterStatus('all');
                }}
                className="inline-flex items-center space-x-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <XCircle className="w-4 h-4" />
                <span>Clear Filters</span>
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredCases.map((caseItem) => (
            <div
              key={caseItem._id || caseItem.id}
              onClick={() => navigate(`/cases/${caseItem._id || caseItem.id}`)}
              className="bg-white rounded-xl shadow-md border-2 border-yellow-300 hover:shadow-xl hover:border-yellow-400 transition-all cursor-pointer group relative overflow-hidden"
            >
              {/* High-Profile Badge */}
              <div className="absolute top-4 right-4 z-10">
                <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white px-4 py-2 rounded-full flex items-center space-x-2 text-xs font-bold shadow-lg">
                  <Star className="w-4 h-4 fill-current" />
                  <span>HIGH-PROFILE</span>
                </div>
              </div>

              {/* Priority Indicator Bar */}
              <div className={`absolute top-0 left-0 right-0 h-1 ${getPriorityDot(caseItem.priority)}`}></div>

              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-yellow-50/30 via-transparent to-transparent pointer-events-none"></div>

              <div className="p-6 relative">
                {/* Case Header */}
                <div className="mb-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 pr-4">
                      <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors line-clamp-2">
                        {caseItem.title}
                      </h3>
                      <p className="text-sm font-mono text-gray-500 font-semibold">
                        {caseItem.caseNumber}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Description */}
                {caseItem.description && (
                  <p className="text-sm text-gray-700 mb-4 line-clamp-2">
                    {caseItem.description}
                  </p>
                )}

                {/* Badges */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className={`px-3 py-1.5 text-xs font-semibold rounded-lg border-2 ${getPriorityColor(caseItem.priority)} shadow-sm`}>
                    {caseItem.priority}
                  </span>
                  <span className={`px-3 py-1.5 text-xs font-semibold rounded-lg border ${getStatusColor(caseItem.status)}`}>
                    {caseItem.status}
                  </span>
                  {caseItem.caseType && (
                    <span className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gray-100 text-gray-800 border border-gray-300">
                      {caseItem.caseType}
                    </span>
                  )}
                </div>

                {/* Case Details */}
                <div className="space-y-3 border-t border-gray-200 pt-4">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <User className="w-4 h-4 text-gray-400" />
                    <span>
                      Assigned to: <span className="font-semibold text-gray-900">
                        {caseItem.assignedTo?.firstName || caseItem.assignedTo?.first_name} {caseItem.assignedTo?.lastName || caseItem.assignedTo?.last_name}
                      </span>
                    </span>
                  </div>

                  {caseItem.teamMembers && caseItem.teamMembers.length > 0 && (
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <Users className="w-4 h-4 text-gray-400" />
                      <span>
                        Team: <span className="font-semibold text-gray-900">
                          {caseItem.teamMembers.length} member{caseItem.teamMembers.length !== 1 ? 's' : ''}
                        </span>
                      </span>
                    </div>
                  )}

                  {caseItem.dueDate && (
                    <div className={`flex items-center space-x-2 text-sm ${isOverdue(caseItem.dueDate) ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                      <Calendar className={`w-4 h-4 ${isOverdue(caseItem.dueDate) ? 'text-red-500' : 'text-gray-400'}`} />
                      <span>
                        {getDaysUntilDue(caseItem.dueDate)}
                        {isOverdue(caseItem.dueDate) && (
                          <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-800 rounded text-xs font-bold">
                            OVERDUE
                          </span>
                        )}
                      </span>
                    </div>
                  )}

                  {caseItem.facility?.name && (
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <Building className="w-4 h-4 text-gray-400" />
                      <span>
                        Facility: <span className="font-semibold text-gray-900">{caseItem.facility.name}</span>
                      </span>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    Created {moment(caseItem.createdAt).format('MMM DD, YYYY')}
                  </span>
                  <div className="flex items-center space-x-1 text-blue-600 font-semibold text-sm group-hover:text-blue-700 transition-colors">
                    <span>View Details</span>
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Spotlight;
